import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavLink from "../components/NavLink.js";
import EvenessLogo from "../components/EvenessLogo.js";
import { ReactComponent as NavbarIcon } from "./../assets/svgs/navbar.svg";

const Navbar = ({
  desktopItems = [],
  mobileItems = [],
  logoTo = "/",
  mobileTitle = "Menu",
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(false);

  useEffect(() => {
    const readHidden = () => {
      setNavHidden(
        typeof document !== "undefined" &&
          document.documentElement.dataset.rotatorNavbarHidden === "1",
      );
    };

    readHidden();
    const obs = new MutationObserver(readHidden);
    obs.observe(document.documentElement, { attributes: true });

    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (navHidden && mobileMenuOpen) setMobileMenuOpen(false);
  }, [navHidden, mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onEsc = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={[
          "sticky top-0 z-50 w-full",
          "transition-all duration-200",
          navHidden
            ? "opacity-0 pointer-events-none -translate-y-2"
            : "opacity-100 translate-y-0",
        ].join(" ")}
      >
        {" "}
        <div className="relative backdrop-blur-[2px]">
          {/* White gradient background */}
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.88)_65.33%,rgba(255,255,255,0)_100%)]" />

          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link
                to={logoTo}
                className="w-[74px] h-[59px] rounded-[8px] flex items-center justify-center bg-white shadow-[0_0_9.38px_0_#0000001A] z-10 "
                aria-label="Home"
              >
                <EvenessLogo className="w-[24px] h-[25px]" />
              </Link>

              {/* Right pill */}
              <div
                className={[
                  "relative h-[50px] md:h-[59px]",
                  "rounded-[10px] bg-white/80 backdrop-blur-md",
                  "border border-white/70",
                  "shadow-[0_0_9.38px_0_#0000001A]",
                  "px-3 md:px-5",
                  "flex items-center",
                  "overflow-hidden",
                ].join(" ")}
              >
                {/* Blurry effect */}
                <div
                  className={[
                    "pointer-events-none absolute inset-0",
                    "bg-[radial-gradient(circle_at_15%_35%,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.0)_55%),radial-gradient(circle_at_45%_20%,rgba(255,255,255,0.75)_0%,rgba(255,255,255,0.0)_55%),radial-gradient(circle_at_75%_40%,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.0)_60%),radial-gradient(circle_at_60%_80%,rgba(255,255,255,0.65)_0%,rgba(255,255,255,0.0)_55%)]",
                    "opacity-60",
                  ].join(" ")}
                />

                {/* Desktop nav */}
                <nav className="relative hidden md:flex items-center gap-8 font-poppins">
                  {desktopItems.map((item) => (
                    <NavLink
                      key={`${item.to}-${item.label}`}
                      to={item.to}
                      end={item.to === "/overview" || item.to === "/blog"}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>

                {/* Icon on large screens */}
                <button
                  type="button"
                  className="relative ml-4 hidden md:inline-flex items-center justify-center w-10 h-10 rounded-[8px] hover:bg-black/5 transition"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                >
                  <NavbarIcon />
                </button>

                {/* Mobile menu button */}
                <button
                  type="button"
                  className="relative md:hidden inline-flex items-center justify-center w-10 h-10 rounded-[8px]"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                >
                  <NavbarIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile nav modal */}
      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          />

          {/* Panel */}
          <div className="absolute right-4 top-4 left-4 rounded-[14px] bg-white border border-[#E6E6E6] shadow-[0_18px_45px_rgba(0,0,0,0.15)] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-[44px] h-[44px] rounded-[8px] border border-[#E6E6E6] flex items-center justify-center bg-white">
                  <EvenessLogo className="w-[20px] h-[20px]" />
                </div>
                <div className="text-[14px] font-medium text-black">
                  {mobileTitle}
                </div>
              </div>

              <button
                type="button"
                className="w-10 h-10 rounded-[8px] border border-[#E6E6E6] flex items-center justify-center"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <span className="text-[18px] leading-none">×</span>
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              {mobileItems.map((item) => {
                const isPrimary = item.variant === "primary";
                return (
                  <Link
                    key={`${item.to}-${item.label}`}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={[
                      "h-[46px] rounded-[10px] px-4 flex items-center text-[14px]",
                      isPrimary
                        ? "bg-black text-white justify-center"
                        : "border border-[#E6E6E6] text-black",
                    ].join(" ")}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Navbar;